// InsightsPage — financial-only. Where the money went: spend trends across a
// rolling window of cleared (approved/exported/synced) receipts, broken down
// by month, category, person, and show. Read-only — no chart libraries, just
// CSS bars alongside real numbers (bars reinforce, never replace them).

import React, { useEffect, useMemo, useState } from 'react';
import BackLink from '../components/BackLink';
import SegTrack from '../components/ui/SegTrack';
import PageLayout from '../components/ui/PageLayout';
import { Panel, StatTile } from '../components/ui/Panel';
import { ListRow, ListStack } from '../components/ui/ListRow';
import { Progress } from '../components/ui/Progress';
import { EmptyState } from '../components/ui/EmptyState';
import { Skeleton, SkeletonGroup } from '../components/ui/Skeleton';
import FadeIn from '../components/ui/FadeIn';
import { apiFetchAuthed } from '../apiHelpers';
import { useToast } from '../components/Toast/ToastContext';
import { formatEventDate } from '../utils/date-utils';
import type { SpendSummary, SpendByCategory, SpendByOwner, SpendByEvent } from './types';

const money = (v: unknown) => (v == null || v === '' ? '—' : `$${Number(v).toFixed(2)}`);
const num = (v: unknown) => Number(v) || 0;

const MONTH_WINDOWS = [3, 6, 12] as const;

const monthLabel = (key: string) => {
  const [y, m] = key.split('-').map(Number);
  return new Date(y, m - 1, 1).toLocaleString('default', { month: 'short', year: 'numeric' });
};

// Shared shape for the category/person/show rank tables: label, count, mono
// total, share-of-window% + a thin proportional bar. Top 12 + "and N more".
interface RankRow { key: string; label: string; sub?: string | null; count: number; total: number; }
function RankTable({ title, rows, grandTotal, emptyLabel }: {
  title: string; rows: RankRow[]; grandTotal: number; emptyLabel: string;
}) {
  const top = rows.slice(0, 12);
  const extra = rows.length - top.length;
  return (
    <Panel title={title} accent="primary">
      {rows.length === 0 ? (
        <EmptyState variant="line">{emptyLabel}</EmptyState>
      ) : (
        <>
          <ListStack
            on="panel"
            label={title}
            items={top}
            getKey={(r) => r.key}
            renderRow={(r) => {
              const share = grandTotal > 0 ? (r.total / grandTotal) * 100 : 0;
              return (
                <ListRow>
                  <span className="block py-0.5">
                    <span className="flex items-center justify-between gap-3">
                      <span className="min-w-0">
                        <span className="block truncate text-sm text-on-surface">{r.label}</span>
                        {r.sub && <span className="block truncate text-xs text-on-surface-variant">{r.sub}</span>}
                      </span>
                      <span className="flex shrink-0 items-center gap-3">
                        <span className="text-xs text-on-surface-variant">{r.count}</span>
                        <span className="font-mono text-sm tabular-nums text-on-surface">{money(r.total)}</span>
                        <span className="w-9 text-right font-mono text-xs tabular-nums text-on-surface-variant">{share.toFixed(0)}%</span>
                      </span>
                    </span>
                    <Progress className="mt-1.5" size="xs" tone="neutral" label={`${r.label} share of spend`} value={share} max={100} />
                  </span>
                </ListRow>
              );
            }}
          />
          {extra > 0 && <div className="px-3 pt-2 text-xs text-on-surface-variant">and {extra} more</div>}
        </>
      )}
    </Panel>
  );
}

export default function InsightsPage() {
  const toast = useToast();
  const [months, setMonths] = useState<number>(6);
  const [data, setData] = useState<SpendSummary | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let live = true;
    setLoading(true);
    apiFetchAuthed(`/reconciliation/spend?months=${months}`)
      .then((d: SpendSummary) => { if (live) setData(d); })
      .catch((e: any) => toast.error(e?.message || 'Could not load insights'))
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [months]);

  const grandTotal = useMemo(() => num(data?.totals.total), [data]);
  const maxMonth = useMemo(() => Math.max(0, ...(data?.byMonth || []).map((m) => num(m.total))), [data]);

  const categoryRows: RankRow[] = useMemo(() => (data?.byCategory || []).map((c: SpendByCategory) => ({
    key: String(c.category_id ?? 'none'), label: c.label, count: num(c.count), total: num(c.total),
  })).sort((a, b) => b.total - a.total), [data]);

  const ownerRows: RankRow[] = useMemo(() => (data?.byOwner || []).map((o: SpendByOwner) => ({
    key: o.user_id, label: o.name, count: num(o.count), total: num(o.total),
  })).sort((a, b) => b.total - a.total), [data]);

  const eventRows: RankRow[] = useMemo(() => (data?.byEvent || []).map((e: SpendByEvent) => ({
    key: e.event_id, label: e.name, sub: e.event_date ? formatEventDate(e.event_date) : null, count: num(e.count), total: num(e.total),
  })).sort((a, b) => b.total - a.total), [data]);

  const empty = !loading && data && num(data.totals.count) === 0;

  return (
    <PageLayout width="narrow" kind="workspace" className="pt-4 lg:pt-6" header={(
      <>
      <BackLink to="/receipts" label="Receipts" className="mb-3" />

      <div className="flex items-center justify-between mb-5">
        <h1 className="flex items-center gap-2.5 text-2xl font-bold text-white">
          <span className="w-1 h-6 rounded-full bg-primary/60" />
          Insights
        </h1>
        <div className="flex items-center gap-1 text-xs">
          <span className="text-on-surface-variant mr-1">Window:</span>
          <SegTrack
            label="Window"
            size="sm"
            options={MONTH_WINDOWS.map((m) => ({ key: String(m), label: `${m}mo` }))}
            value={String(months)}
            onChange={(k) => setMonths(Number(k))}
          />
        </div>
      </div>
      </>
    )}>

      <FadeIn key={loading && !data ? 'loading' : empty ? 'empty' : data ? 'data' : 'none'}>
      {loading && !data ? (
        <SkeletonGroup label="Loading insights" className="space-y-2">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-14 rounded-panel" />)}</SkeletonGroup>
      ) : empty ? (
        <EmptyState title="No cleared receipts in this window">
          Approved, exported, and synced receipts show up here.
        </EmptyState>
      ) : data ? (
        <div className="space-y-8">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <StatTile label="Total spend" value={money(data.totals.total)} />
            <StatTile label="Receipts" value={String(num(data.totals.count))} />
            <StatTile label="Uncategorized" value={money(data.totals.uncategorized)} tone={num(data.totals.uncategorized) > 0 ? 'warn' : 'neutral'} />
            <StatTile label="Untagged to a show" value={money(data.totals.untaggedToEvent)} tone={num(data.totals.untaggedToEvent) > 0 ? 'warn' : 'neutral'} />
          </div>

          <Panel title="By month" accent="primary">
            <div className="space-y-2">
              {data.byMonth.map((m) => {
                const total = num(m.total);
                return (
                  <div key={m.month} className="flex items-center gap-3">
                    <span className="w-20 shrink-0 text-xs text-on-surface-variant">{monthLabel(m.month)}</span>
                    <Progress className="flex-1" size="lg" label={`${monthLabel(m.month)} spend`} value={total} max={maxMonth} />
                    <span className="w-20 shrink-0 text-right font-mono text-sm tabular-nums text-on-surface">{money(total)}</span>
                    <span className="w-14 shrink-0 text-right text-[11px] text-on-surface-variant">{num(m.count)} rcpt</span>
                  </div>
                );
              })}
            </div>
          </Panel>

          <RankTable title="By category" rows={categoryRows} grandTotal={grandTotal} emptyLabel="No categorized spend in this window." />
          <RankTable title="By person" rows={ownerRows} grandTotal={grandTotal} emptyLabel="Nothing to show." />
          <RankTable title="By show" rows={eventRows} grandTotal={grandTotal} emptyLabel="No receipts tagged to a show in this window." />
        </div>
      ) : null}
      </FadeIn>
    </PageLayout>
  );
}
